<script lang="ts">
  import { onDestroy, onMount, untrack } from "svelte"
  import { mdiContentSave, mdiRestore } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { registerNavigationGuard } from "@kontextwerk/web-sdk/core/routing/navigation"
  import { updateChannelTemplates } from "../../../../../../../../../api/kehrblatt/letters"
  import type { EditableDigitalChannel } from "../../../config"
  import { readDigitalTemplate, digitalTemplateHelp } from "./_logic/digitalTemplates"
  import {
    PHONE_TEMPLATE_HELPER_TEXT,
    SMS_SINGLE_SEGMENT_LIMIT,
    TEMPLATE_HELPER_TEXT,
    buildSmsLengthMessage,
    emailBodyTemplateTokens,
    emailSubjectTemplateTokens,
    phoneTemplateTokens,
    smsTemplateTokens,
  } from "../../_logic/templates"

  const { organizationId, policy, channel, purpose, canManage, onPolicyUpdated } = $props<{
    organizationId: string
    policy: OrganizationContactPolicy
    channel: EditableDigitalChannel
    purpose: string
    canManage: boolean
    onPolicyUpdated: (policy: OrganizationContactPolicy) => void
  }>()
  const initial = untrack(() => readDigitalTemplate(policy, channel, purpose))
  let baseline = $state(initial)
  let draft = $state(initial ? { ...initial } : null)
  let saving = $state(false)
  let error = $state<string | null>(null)
  let notice = $state<string | null>(null)
  let active = true
  const dirty = $derived(JSON.stringify(draft) !== JSON.stringify(baseline))
  const disabled = $derived(saving || !canManage)
  const help = $derived(digitalTemplateHelp[channel])
  const smsLength = $derived(buildSmsLengthMessage(draft?.body ?? ""))
  onDestroy(() => {
    active = false
  })

  const reset = () => {
    if (disabled) return

    const defaults = readDigitalTemplate(policy, channel, purpose, true)

    if (!defaults) {
      error = "Die Standardvorlage ist nicht verfügbar. Bitte laden Sie die Seite erneut."

      return
    }

    draft = defaults
    error = null
    notice = null
  }

  const save = async () => {
    if (!active || disabled || !dirty || !draft) return

    error = null
    notice = null

    if (channel === "sms" && !smsLength.withinLimit) {
      error = `SMS-Text ist zu lang. Erlaubt sind maximal ${SMS_SINGLE_SEGMENT_LIMIT} Zeichen.`

      return
    }

    if (Object.values(draft).some((value) => value.length > 20000)) {
      error = "Ein Vorlagentext darf höchstens 20.000 Zeichen enthalten."

      return
    }

    const targetOrganization = organizationId
    const selectedChannel = channel
    const selectedPurpose = purpose
    saving = true
    try {
      const updated = await updateChannelTemplates(targetOrganization, selectedChannel, {
        [selectedPurpose]: { ...draft },
      })

      if (
        !active ||
        targetOrganization !== organizationId ||
        selectedChannel !== channel ||
        selectedPurpose !== purpose
      )
        return

      const normalized = readDigitalTemplate(updated, selectedChannel, selectedPurpose)

      if (!normalized) throw new Error("Missing normalized template")

      baseline = normalized
      draft = { ...normalized }
      onPolicyUpdated(updated)
      notice = "Vorlage gespeichert."
    } catch {
      if (active && organizationId === targetOrganization) {
        error = "Vorlage konnte nicht gespeichert werden. Ihre Änderungen bleiben erhalten."
      }
    } finally {
      if (active) saving = false
    }
  }

  onMount(() => {
    const unregister = registerNavigationGuard(
      `template-${organizationId}-${channel}-${purpose}`,
      (_to, _options, resume) => {
        if (saving) return false

        if (!dirty) return true

        void newApproval({
          title: "Ungespeicherte Vorlage",
          description: "Möchten Sie die Vorlage verlassen und Ihre Änderungen verwerfen?",
          approvalLabel: "Verwerfen",
          declineLabel: "Weiter bearbeiten",
          approvalCallback: () => {
            if (!active || saving) return

            draft = baseline ? { ...baseline } : null
            resume?.()
          },
        })

        return false
      },
    )
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (dirty || saving) {
        event.preventDefault()
        event.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", beforeUnload)

    return () => {
      unregister()
      window.removeEventListener("beforeunload", beforeUnload)
    }
  })
</script>

<section aria-label={help.title} class="template-editor">
  <SectionTitle
    title={help.title}
    brightBackground={true}
    small={true}
    helpModalTextId={help.helpId}
  />
  {#if !draft}
    <p role="alert">Die Vorlage ist nicht verfügbar. Bitte laden Sie die Seite erneut.</p>
  {:else}
    <div class="template-form form">
      {#if channel === "email"}
        <Input
          type="tagbased_input"
          placeholder="Betreff"
          bind:value={draft.subject}
          options={emailSubjectTemplateTokens}
          tokenSyntax="single"
          brightBackground={true}
          {disabled}
          helperText={TEMPLATE_HELPER_TEXT}
        />
        <Input
          type="tagbased_input"
          placeholder="Nachricht"
          bind:value={draft.body}
          options={emailBodyTemplateTokens}
          tokenSyntax="single"
          brightBackground={true}
          {disabled}
          helperText={TEMPLATE_HELPER_TEXT}
        />
      {:else if channel === "sms"}
        <Input
          type="tagbased_input"
          placeholder="Text für SMS"
          bind:value={draft.body}
          options={smsTemplateTokens}
          tokenSyntax="single"
          brightBackground={true}
          {disabled}
          helperText={`${smsLength.helperText} ${TEMPLATE_HELPER_TEXT}`}
        />
      {:else}
        <Input
          type="tagbased_input"
          placeholder={purpose === "outbound"
            ? "Telefonansprache ausgehend"
            : "Telefonansprache eingehend"}
          bind:value={draft.opening}
          options={phoneTemplateTokens}
          tokenSyntax="single"
          brightBackground={true}
          {disabled}
          helperText={PHONE_TEMPLATE_HELPER_TEXT}
        />
      {/if}
    </div>
    {#if error}<p role="alert">{error}</p>{/if}
    {#if notice}<p role="status">{notice}</p>{/if}
    {#if canManage}
      <div class="actions">
        <CTA
          type="primaryButton"
          content="Speichern"
          icon={mdiContentSave}
          onClick={save}
          disabled={saving || !dirty}
          options={{
            label: "Vorlagenoptionen",
            disabled,
            items: [
              {
                id: "default-template",
                label: "Standardvorlage übernehmen",
                icon: mdiRestore,
                onClick: reset,
              },
            ],
          }}
        />
      </div>
    {/if}
  {/if}
</section>

<style>
  .template-editor {
    display: grid;
    gap: 1rem;
    min-width: 0;
    color: var(--text-invers-100);
  }
  .template-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-width: 0;
  }
  .actions {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.65rem;
  }
  .actions :global(button.cta) {
    max-width: 100%;
  }
  .actions :global(button.cta p) {
    white-space: normal;
  }
  [role="alert"] {
    border-left: 3px solid var(--red-300);
    padding: 0.6rem 0.8rem;
  }
</style>
